import { useState } from 'react'
import { motion } from 'framer-motion'
import { LCARSPanel, LCARSButton, LCARSBar } from '@enterpress/holomatrix'
import { Mail, CheckCircle, Loader2, Rocket, Users, Globe } from 'lucide-react'
import { addToWaitlist, ApiError } from '../lib/api'
import { trackWaitlistSignup } from '../lib/analytics'
import { useTranslation } from '../i18n/index'

/**
 * Waitlist Section - Community Building
 * 
 * Email capture with Cloud Run API backend, protected by Cloud Armor
 */
export function Waitlist() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email || !email.includes('@')) {
      setErrorMessage('Please enter a valid email address')
      setStatus('error')
      return
    }

    setStatus('loading')
    
    try {
      await addToWaitlist(email)
      setStatus('success')
      setEmail('')
      trackWaitlistSignup(true)
    } catch (error) {
      console.error('Waitlist signup error:', error)
      
      let errorCode = 'unknown'
      let message = 'Something went wrong. Please try again.'
      
      if (error instanceof ApiError) {
        errorCode = error.code || 'api_error'
        
        switch (error.code) {
          case 'RATE_LIMITED':
            message = 'Easy there, captain! Please wait a moment before trying again.'
            errorCode = 'rate_limited'
            break
          case 'NETWORK_ERROR':
            message = 'Connection interrupted. Please check your network and try again.'
            errorCode = 'network_error'
            break
          case 'DUPLICATE_EMAIL':
            message = 'You\'re already on the waitlist! We\'ll be in touch soon.'
            errorCode = 'duplicate'
            break
          default:
            message = error.message
        }
      }
      
      setErrorMessage(message)
      trackWaitlistSignup(false, errorCode)
      setStatus('error')
    }
  }

  return (
    <section id="waitlist" className="py-24 px-6 relative">
      {/* Background accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[rgba(0,255,136,0.02)] to-transparent pointer-events-none" />
      
      <div className="max-w-4xl mx-auto relative">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <LCARSBar color="green" size="md" className="w-20" />
            <h2 className="text-sm font-bold tracking-[4px] uppercase text-[#00FF88]">
              {t('waitlist.title')}
            </h2>
            <LCARSBar color="green" size="md" className="w-20" />
          </div>
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t('waitlist.subtitle')}
          </h3>
          <p className="text-lg text-[rgba(232,244,248,0.6)] max-w-2xl mx-auto">
            {t('waitlist.description')}
          </p>
        </motion.div>

        {/* Signup form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <LCARSPanel title={t('waitlist.formTitle')} color="green" className="max-w-xl mx-auto">
            <div className="p-8">
              {status === 'success' ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-8"
                >
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[rgba(0,255,136,0.15)] flex items-center justify-center">
                    <CheckCircle size={32} className="text-[#00FF88]" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-2">
                    {t('waitlist.successTitle')}
                  </h4>
                  <p className="text-[rgba(232,244,248,0.6)] mb-6">
                    {t('waitlist.successDesc')}
                  </p>
                  <LCARSButton 
                    color="cyan" 
                    size="sm"
                    onClick={() => setStatus('idle')}
                  >
                    {t('waitlist.ctaAnother')}
                  </LCARSButton>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="mb-6">
                    <label 
                      htmlFor="email" 
                      className="block text-xs font-bold tracking-wider uppercase text-[rgba(232,244,248,0.6)] mb-2"
                    >
                      {t('waitlist.labelEmail')}
                    </label>
                    <div className="relative">
                      <Mail 
                        size={18} 
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-[rgba(232,244,248,0.4)]" 
                      />
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value)
                          if (status === 'error') setStatus('idle')
                        }}
                        placeholder={t('waitlist.placeholderEmail')}
                        className="w-full pl-12 pr-4 py-3 bg-[rgba(0,20,40,0.6)] border border-[rgba(0,255,136,0.3)] rounded-lg text-white placeholder-[rgba(232,244,248,0.3)] focus:outline-none focus:border-[#00FF88] focus:shadow-[0_0_20px_rgba(0,255,136,0.2)] transition-all"
                        disabled={status === 'loading'}
                      />
                    </div>
                    {status === 'error' && (
                      <p className="mt-2 text-sm text-[#FF6B6B]">{errorMessage}</p>
                    )}
                  </div>

                  <LCARSButton 
                    color="green" 
                    className="w-full"
                    onClick={() => handleSubmit({ preventDefault: () => {} } as React.FormEvent)}
                  >
                    {status === 'loading' ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        {t('waitlist.ctaRegistering')}
                      </>
                    ) : (
                      <>
                        <Rocket size={18} />
                        {t('waitlist.ctaJoin')}
                      </>
                    )}
                  </LCARSButton>

                  <p className="mt-4 text-xs text-center text-[rgba(232,244,248,0.4)]">
                    {t('waitlist.footerSpam')}
                  </p>
                </form>
              )}
            </div>
          </LCARSPanel>
        </motion.div>

        {/* Community stats */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-12 flex flex-wrap justify-center gap-8"
        >
          <div className="flex items-center gap-3 text-[rgba(232,244,248,0.5)]">
            <Users size={18} />
            <span className="text-sm">{t('waitlist.statsBuilders')}</span>
          </div>
          <div className="flex items-center gap-3 text-[rgba(232,244,248,0.5)]">
            <Globe size={18} />
            <span className="text-sm">{t('waitlist.statsServing')}</span>
          </div>
        </motion.div>
      </div>

    </section>
  )
}
